import React, { useState } from 'react'
import { Building2, Crown, Layers } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { cn } from '@/lib/utils'
import { listingColor } from '@/lib/listing-colors'
import { ABBREV_TOOLTIPS } from '@/lib/plain-language'

/**
 * BY LISTING: each listing's own position in a combined inventory.
 *
 * Every other band on the lead's dashboard is the BUILDING: every listing's bookings over the
 * combined room count. This card is the one place a listing's own share shows, by name, for the
 * pickup table's own windows: its share of the building's room-nights sold, its rooms and revenue
 * on the books, its ADR and its 7-day pickup. The Building row is the pickup table's row for the
 * same window, so the two cards agree.
 *
 * Every string arrives composed by the server (DashboardInsightsMutation#by_listing): labels,
 * money, shares and pickup, and the direction a pickup moved. The card picks a window and prints;
 * it adds, compares and rounds nothing. A listing the person cannot open has no row (the server
 * leaves it out) while its bookings stay in the Building row, as on every band.
 */

export interface ByListingFigures {
  roomsOtb: number
  roomsOtbLabel: string
  sharePct: number
  shareLabel: string
  revenueOtb: number
  revenueLabel: string
  adr: number
  adrLabel: string
  pickupRooms7d: number
  pickupLabel: string
  pickupTone: 'up' | 'down' | 'none'
}

export interface ByListingRow extends ByListingFigures {
  propertyId: number
  name: string
  isLead: boolean
  role: string
  /** The listing's place in the combined inventory's own order: its colour, on this card and the chart. */
  slot: number
}

export interface ByListingWindow {
  key: string
  label: string
  rangeLabel: string
  listings: ByListingRow[]
  building: ByListingFigures & { name: string }
}

export interface ByListingData {
  pickupWindowDays: number
  pickupSince: string
  windows: ByListingWindow[]
}

const PICKUP_TONE: Record<ByListingFigures['pickupTone'], string> = {
  up: 'text-emerald-600',
  down: 'text-rose-600',
  none: 'text-muted-foreground',
}

const numericHead = 'h-10 px-4 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

function Otb() {
  return (
    <abbr title={ABBREV_TOOLTIPS.OTB} className="cursor-help no-underline decoration-dotted underline-offset-2 hover:underline">
      OTB
    </abbr>
  )
}

/** The window picker: four buttons that change what the table shows, never the page. */
function WindowPicker({
  windows,
  selected,
  onSelect,
}: {
  windows: ByListingWindow[]
  selected: string
  onSelect: (key: string) => void
}) {
  return (
    <div role="group" aria-label="Window" className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted p-1">
      {windows.map((w) => (
        <button
          key={w.key}
          type="button"
          aria-pressed={w.key === selected}
          onClick={() => onSelect(w.key)}
          className={cn(
            'cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            w.key === selected ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {w.label}
        </button>
      ))}
    </div>
  )
}

function FigureCells({ figures, color, emphasis = false }: { figures: ByListingFigures; color?: string; emphasis?: boolean }) {
  const weight = emphasis ? 'font-bold' : 'font-semibold'
  return (
    <>
      <TableCell className="px-4 py-3 text-right">
        <p className={cn('text-sm text-foreground tabular-nums', weight)}>{figures.shareLabel}</p>
        {color && (
          // The bar's length is the server's own share figure, used as it was sent.
          <div className="ml-auto mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className="h-full rounded-full" style={{ width: `${figures.sharePct}%`, backgroundColor: color }} />
          </div>
        )}
      </TableCell>
      <TableCell className={cn('px-4 py-3 text-right text-sm text-foreground tabular-nums', weight)}>
        {figures.roomsOtbLabel}
      </TableCell>
      <TableCell className={cn('px-4 py-3 text-right text-sm text-foreground tabular-nums', weight)}>
        {figures.revenueLabel}
      </TableCell>
      <TableCell className={cn('px-4 py-3 text-right text-sm text-foreground tabular-nums', weight)}>
        {figures.adrLabel}
      </TableCell>
      <TableCell className={cn('px-4 py-3 text-right text-sm tabular-nums', weight, PICKUP_TONE[figures.pickupTone])}>
        {figures.pickupLabel}
      </TableCell>
    </>
  )
}

export default function ByListingCard({ data }: { data: ByListingData }) {
  const [selectedKey, setSelectedKey] = useState(data.windows[0]?.key ?? '')
  const current = data.windows.find((w) => w.key === selectedKey) ?? data.windows[0]
  if (!current) return null

  return (
    <DashboardCard
      title="By listing"
      description={`Each listing's own bookings, ${current.rangeLabel}. Shares are of the building's room-nights sold.`}
      icon={<Layers className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "Bookings on every listing count toward the building, so the rest of this page reads the building. Here each listing's own bookings show by name: its share of the building's room-nights sold, its revenue, its ADR (its own revenue divided by its own room-nights) and the rooms it picked up in the last 7 days. The Building row is the whole building, the same figures as the pickup table.",
        learnMore: '/guides/dashboard#by-listing',
      }}
      padded={false}
    >
      <div className="border-b border-border/40 px-5 py-3">
        <WindowPicker windows={data.windows} selected={current.key} onSelect={setSelectedKey} />
      </div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Listing
            </TableHead>
            <TableHead className={numericHead}>Share of building</TableHead>
            <TableHead className={numericHead}>Rooms <Otb /></TableHead>
            <TableHead className={numericHead}>Revenue <Otb /></TableHead>
            <TableHead className={numericHead}>ADR</TableHead>
            <TableHead className={numericHead}>Pickup {data.pickupWindowDays}d</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {current.listings.map((row) => {
            const color = listingColor(row.slot)
            const Icon = row.isLead ? Crown : Building2
            return (
              <TableRow key={row.propertyId}>
                <TableCell className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{row.name}</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {row.role}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <FigureCells figures={row} color={color} />
              </TableRow>
            )
          })}
          <TableRow className="border-t-2 border-border/60 bg-muted/20 hover:bg-muted/20">
            <TableCell className="px-4 py-3">
              <p className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                {current.building.name}
              </p>
            </TableCell>
            <FigureCells figures={current.building} emphasis />
          </TableRow>
        </TableBody>
      </Table>
    </DashboardCard>
  )
}
